/**
 * @file DuplicateButton.jsx
 * @description A button component for triggering duplication actions on elements, designed to be reusable and customizable.
 * @requires React, PropTypes, react-i18next
 * @exports DuplicateButton
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ActionButton from './ActionButton';

/**
 * @typedef {Object} DuplicateButtonProps
 * @property {Function} action - Callback function to handle the duplication action. Should accept an `elementId`.
 * @property {string} elementId - Identifier of the element to be duplicated.
 * @property {string} [label='Duplicate this element'] - Text label displayed on the button.
 * @property {string} [icon='copy'] - Icon displayed on the button.
 * @property {string} [tooltipText] - Tooltip text displayed on hover. Defaults to localized "Duplicate" text.
 * @property {string} [color='blue'] - Color theme of the button.
 */

/**
 * DuplicateButton is a reusable component wrapping the ActionButton, specialized for duplication tasks.
 *
 * @component
 * @param {DuplicateButtonProps} props - Component properties.
 * @returns {JSX.Element} A styled button component with an action and tooltip for duplicating elements.
 * @throws {Error} If required props `action` or `elementId` are missing.
 *
 * @example
 * const handleDuplicate = (id) => console.log(`Duplicating element with ID: ${id}`);
 * 
 * // Basic usage
 * <DuplicateButton
 *   action={handleDuplicate}
 *   elementId="12345"
 * />
 *
 * // Customized button
 * <DuplicateButton
 *    action={handleDuplicate}
 *    elementId="12345"
 *    label="Duplicate"
 *    icon="duplicate"
 *    tooltipText="Click to duplicate"
 *    color="secondary"
 * />
 */
const DuplicateButton = ({ 
    action, 
    elementId, 
    label = 'Duplicate this element', 
    icon = 'copy', 
    tooltipText, 
    color = 'blue' 
}) => {
    if (!action) {
        throw new Error("DuplicateButton: Missing required prop 'action'. Expected a function.");
    }

    if (!elementId) {
        throw new Error("DuplicateButton: Missing required prop 'elementId'. Expected a string identifier.");
    }

    const { t } = useTranslation();

    const computedTooltipText = useMemo(() => tooltipText || t('Popup_Duplicate'), [tooltipText, t]);
    
    return (
        <ActionButton
            action={action}
            elementId={elementId}
            label={label}
            icon={icon}
            tooltipText={computedTooltipText}
            color={color}
        />
    );
}

DuplicateButton.propTypes = {
    action: PropTypes.func.isRequired,
    elementId: PropTypes.string.isRequired,
    label: PropTypes.string,
    icon: PropTypes.string,
    tooltipText: PropTypes.string,
    color: PropTypes.string
};

export default DuplicateButton;
